<template>
	<div>
		<div>
				<v-container fluid>
					<v-row dense>
						<v-col sm="12" md="6" >
							<AppCard hide-header class="pa-2" style="height: 100%">
								<h3><span style="color: #4caf50">BILLING</span> Organization Connections</h3>
								<v-divider class="mt-2 mb-2"></v-divider>
								<template v-if="organizationBillingStatus">
								<div><strong>{{$store.getters.selectedOrganizationName}}</strong> is a <strong>{{ organizationBillingStatus.label }}</strong></div>
								<div>{{ organizationBillingStatus.description }}</div>
								<div
									v-if="organizationBillingStatus.type === 1 && billingInfo.billToOrgId"
									class="mt-2"
								>
									Parent Billing Organization: {{billingInfo.billToOrgName}} ({{billingInfo.billToOrgId}})
								</div>
								<div 
									v-if="organizationBillingStatus.type === 2 && billingChildOrgs && billingChildOrgs.length > 0"
									class="mt-2"
								>
									Child Billing Organizations Count: {{billingChildOrgs.length}}
									<ul>
										<li v-for="org in billingChildOrgs">{{org.organizationName}} ({{org.organizationId}})</li>
									</ul>
								</div>
								</template>
							</AppCard>
						
						</v-col>
						<v-col sm="12" md="6" >
							<AppCard hide-header class="pa-2">
								<h3><span style="color: #2196f3">ACCESS</span> Organization Connections</h3>
								<v-divider class="mt-2 mb-2"></v-divider>
								<h4>Parent Organizations</h4>
								<app-simple-table
									v-if="orgConnects"
									:items="orgConnects.parentOrgConnects"
									:headers="parentOrgConnectionHeaders"
								>
									<template v-slot:no-data>
										<div style="display: flex; justify-content: center; padding: 20px">
											No access parent organizations
										</div>
									</template>
								</app-simple-table>
								<h4 class="mt-4">Child Organizations</h4>
								<app-simple-table
									v-if="orgConnects && orgConnects.childOrgConnects.length > 0"
									:items="orgConnects.childOrgConnects"
									:headers="childOrgConnectionHeaders"
								>
									<template v-slot:no-data>
										<div style="display: flex; justify-content: center; padding: 20px">
											No access child organizations
										</div>
									</template>
								</app-simple-table>
								<div v-else>
									No Access Child Organizations
								</div>
							
							</AppCard>
						
						</v-col>
						<v-col sm="12" lg="6">
							<AppCard hide-header class="pa-2">
								<h3>Invoices</h3>
								<v-divider class="mt-2 mb-2"></v-divider>
								<app-simple-table
									:items="invoices"
									:headers="invoicesTableHeaders"
								>
									<template #item.paidTotal="{item}">
										
										<div v-if="item.payment && !item.isRefunded">
											{{ item.paidTotal }}
											<app-chip  color="green" label small class="ml-2">Paid</app-chip>
										</div>
										<div v-else-if="item.isRefunded">
											<app-chip  color="yellow" label small class="ml-2">Payment Refunded</app-chip>
										</div>
										<div v-else-if="item.deletedOn">
											<app-chip  color="gray" label small class="ml-2">VOIDED</app-chip>
										</div>
										<div v-else>
											<app-chip :color="item.isPastDue ? 'red' : 'blue'" label small>{{ item.isPastDue ? 'Past Due' : 'Outstanding' }}</app-chip>
										</div>
									
									</template>
								</app-simple-table>
							</AppCard>
						</v-col>
					</v-row>
				</v-container>
				

			
		</div>
	</div>
</template>

<script>
import {getInvoices, getProfileUsageHistory, getProductSubscription} from "@/services/billing.service";
import moment from "moment";
import {formatMoney} from "@/js/shared/helper";
import {packageTiers, orgConnectStatusSelectList, orgConnectTypeSelectList} from "@/js/shared/constants";

export default {
	name: "OrgManagementDashboard",
	props: {
		billingInfo: { type: Object, default: null },
		billingChildOrgs: { type: Array, default: () => [] },
		accessParentOrgs: { type: Array, default: () => [] },
		accessChildOrgs: { type: Array, default: () => [] },
	},
	data() {
		return {
			currentSubscription: null,
			usageHistory: null,
			invoices: [],
			invoicesTableHeaders: [
				{
					text: 'Invoice Number',
					value: 'displayNumber'
				},
				{
					text: 'Invoice Date',
					value: 'issuedOn'
				},
				{
					text: 'Due Date',
					value: 'displayDueDate'
				},
				{
					text: 'Total',
					value: 'total'
				},
				{
					text: 'Payment',
					value: 'paidTotal'
				},
			],
			parentOrgConnectionHeaders: [
				{
					text: 'ID',
					value: 'parentOrganizationId'
				},
				{
					text: 'Name',
					value: 'parentOrganizationName'
				},
				{
					text: 'Connection Type',
					value: 'orgConnectTypeLabel'
				},
				{
					text: 'Connection Status',
					value: 'orgConnectStatusTypeLabel'
				},
			],
			childOrgConnectionHeaders: [
				{
					text: 'ID',
					value: 'childOrganizationId'
				},
				{
					text: 'Name',
					value: 'childOrganizationName'
				},
				{
					text: 'Connection Type',
					value: 'orgConnectTypeLabel'
				},
				{
					text: 'Connection Status',
					value: 'orgConnectStatusTypeLabel'
				},
			],
			
		}
	},
	mounted() {
		// billingInfo, billingChildOrgs, accessParentOrgs and accessChildOrgs come
		// in as props from OrgManagement.vue — no need to refetch here.
		this.getCurrentSubscription();
		this.getUsageHistory();
		this.getInvoices()
	},
	methods: {
		async getInvoices() {
			const response = await getInvoices();
			
			let invoices = []
			
			if (response.isSuccess) {
				invoices = response.invoices;
			}
			
			invoices.forEach(invoice => {
				const {id, displayInvoiceDate, displayDueDate, total, issuedOn} = invoice;
				let format = "M/D/YYYY";
				invoice.id = id;
				invoice.displayNumber = id + (invoice.deletedOn ? ' (VOIDED)' : (invoice.isRefunded ? ' (REFUNDED)' : (invoice.isNotSent ? ' (NOT SENT)' : '')));
				invoice.displayInvoiceDate = moment(displayInvoiceDate).format(format);
				invoice.displayDueDate = moment(displayDueDate).format(format);
				invoice.total = formatMoney(total, 2);
				invoice.totalNumeric = total;
				invoice.paidTotal = invoice.payment ? (formatMoney(invoice.payment.total, 2) + (invoice.isRefunded ? ' (REFUNDED)' : '')) : '$0.00';
				invoice.paidTotalNumeric = invoice.payment ? invoice.payment.total : 0.0;
				invoice.isPastDue = !invoice.payment && moment().isAfter(moment(displayDueDate)) && !invoice.deletedOn;
				invoice.issuedOn = moment(issuedOn).format(format);
				
				if (invoice.deletedOn)
					invoice.displayRange = invoice.id + ' (VOID)'
				else if (invoice.isNotSent)
					invoice.displayRange = invoice.id + ' (Visible on ' + invoice.displayInvoiceDate + ')'
				else if (invoice.payment && invoice.payment.total === total)
					invoice.displayRange = invoice.id + ' (Paid on ' + moment(invoice.payment.paymentDate).format(format) + ')'
				else
					invoice.displayRange = invoice.id + ' (Due ' + invoice.displayDueDate + ')'
			});
			invoices.sort(function (a,b) {
				return b.id - a.id;
			});
			
			this.invoices = invoices.slice(0,5)
			
		},
		async getCurrentSubscription() {

			this.isDataLoaded = false;

			const response = await getProductSubscription(false, false);
			
			if (response.isSuccess) {

				if (response.model.sports)
					this.sportsSubscriptionEnabled = true;
				
				if (response.model.clinical)
					this.clinicalSubscriptionEnabled = true;
				
				this.currentSubscription = response.model
				
				// this.model = response.model;
				// this.organizationName = response.organizationName;
				// this.isUpcomingSubscription = response.isUpcomingSubscription;
				// this.isPreviousSubscription = response.isPreviousSubscription;
			}
			
			this.isDataLoaded = true;
		},
		async getUsageHistory() {
			
			try {
				
				let response = await getProfileUsageHistory();
			
				if (response.isSuccess) {
					this.usageHistory = response
					// this.sportsProfileUsageHistory = response.sportsUsageItems;
					// this.sportsProfilesRemaining = response.sportsProfilesRemaining;
					// this.clinicalProfileUsageHistory = response.clinicalUsageItems;
					// this.clinicalProfilesRemaining = response.clinicalProfilesRemaining;
					// this.hasMultipleOrganizationUsage = response.hasMultipleOrganizationUsage
					
					
					
				}
				
				
			}
			catch (e) {
				
			}
		},
		dateFormatter(date) {
			return moment(date).format("MM/DD/YYYY")
		},
		
	},
	computed: {
		orgConnects() {
			// Synthesize the wrapper shape the template expects from the parent's
			// accessParentOrgs / accessChildOrgs props, and add the orgConnect*Label
			// fields the v-data-tables' headers reference.
			const withLabels = (arr) => (arr || []).map(x => ({
				...x,
				orgConnectTypeLabel: orgConnectTypeSelectList.find(y => y.value === x.orgConnectTypeId)?.text,
				orgConnectStatusTypeLabel: orgConnectStatusSelectList.find(y => y.value === x.orgConnectStatusTypeId)?.text,
			}));
			return {
				parentOrgConnects: withLabels(this.accessParentOrgs),
				childOrgConnects: withLabels(this.accessChildOrgs),
			};
		},
		packageTier() {
			if (!this.currentSubscription) return null
			let packageTier = this.currentSubscription.sports?.packageTier ? this.currentSubscription.sports?.packageTier : this.currentSubscription.clinical?.packageTier;
			// let something = packageTiers;
			// debugger;
			return packageTiers.find(x => x.value === packageTier).text
		},
		organizationBillingStatus() {
			if (!this.billingInfo || !this.billingChildOrgs) return null
			
			if (this.billingInfo.billToOrgId) return {
				type: 1,
				label: "Billing Child",
				description: "A Billing Child Organization's usage counts toward it's Billing Parent Organizations usage or it's invoices are paid for by the Billing Parent Organization."
			}
			else if (this.billingChildOrgs.organizations) return {
				type: 2,
				label: "Billing Parent",
				description: "A Billing Parent Organization is responsible for paying its own invoices as well as the invoices for all child organizations."
			}
			else return {
				type: 3,
				label: "Standard Billing",
				description: "A standard billing organization is responsible for paying its own invoices. There is no parent organization to bill to and it has no child organizations that it is paying for."
			}
		}
	}
}
</script>

<style scoped>

</style>